import { useEffect, useRef } from 'react';
import type { ChatMessage } from '../lib/data';
import '../chat.css';

/**
 * What a worker has said, in the same window the desk uses.
 *
 * It is READ-ONLY on purpose. The owner talks to Brunel in one place, and a
 * worker feed is a filtered view of that one stream - a box to type in here
 * would be a way to give a worker an order Command never saw.
 *
 * Anything that arrives with line breaks in it came off a command line, so it
 * is set in monospace and left alone rather than reflowed into prose.
 */
export function WorkerConsole({ workerNumber, messages, empty }: {
  workerNumber: number | null;
  messages: readonly ChatMessage[];
  empty: string;
}) {
  const scroller = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const box = scroller.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [messages.length]);

  const who = workerNumber === null ? 'Worker' : `Worker ${workerNumber}`;
  return (
    <div className="transcript" ref={scroller} aria-label="What the worker has said" aria-live="polite">
      {messages.length === 0
        ? <p className="chat-empty">{empty}</p>
        : messages.map((message) => (
          <div key={message.id} className="turn">
            <span className="byline">{`${who} · #${message.seq}`}</span>
            <div className={message.content.includes('\n') ? 'bubble console' : 'bubble'}>{message.content}</div>
          </div>
        ))}
    </div>
  );
}
